import { DataSettings, DatasourceModel } from '../datasources.js';
import { SDKModel } from '../../core/model.js';
export class XmTemplateModel extends SDKModel {
    static templateToDatasource(template, libraryId, tenantName) {
        return new DatasourceModel({
            libraryId,
            id: template.templateId,
            name: template.fullName.split('/').pop(),
            description: '',
            type: 'xmTemplate',
            settings: DataSettings({ jsonpath: '$' }),
            definition: template.fields.nodes,
            sample: {},
            externalSourceId: tenantName
        });
    }
    static generateSampleFromDefinition(definition) {
        return definition === null || definition === void 0 ? void 0 : definition.reduce((obj, field) => ({ ...obj, [field.name]: populateField(field) }), {});
    }
    static async fetchAndFormatSample(datasource, tenantName, sitecoreUrlPostfix, authorization) {
        var _a, _b, _c;
        const authoringApiUrl = `https://xmc-${tenantName}.${sitecoreUrlPostfix}/sitecore/api/authoring/graphql/v1`;
        const body = {
            query: `query {
        search(
          query:{
            index: "sitecore_master_index"
            paging: {
              pageSize: 10
            }
            searchStatement: {
              criteria: [
                { criteriaType: CONTAINS, field: "_templates", value: "${datasource.id}" }
              ]
            }
          }
        )
        {
          results {
            innerItem {
              ${generateGraphQLFields((datasource.conflictDefinition || datasource.definition))}
            }
          }
        }
      }`
        };
        const items = await datasource.sdk.fetchExternalJSON(authoringApiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: authorization },
            body: JSON.stringify(body)
        });
        if ((_c = (_b = (_a = items === null || items === void 0 ? void 0 : items.data) === null || _a === void 0 ? void 0 : _a.search) === null || _b === void 0 ? void 0 : _b.results) === null || _c === void 0 ? void 0 : _c.length) {
            const item = pickMostContentfulItem(items.data.search.results);
            const result = {};
            // format item
            await Promise.all(Object.keys(item).map(async (fieldName) => {
                var _a;
                const field = (datasource.conflictDefinition || datasource.definition).find((field) => field.name === fieldName);
                // Skip fields that don't exist in the definition or have null values
                if (!field || !item[fieldName]) {
                    return;
                }
                // turn xml items (coming from authoring api) to json
                const json = xmlToJSON(item[fieldName].value);
                // enrich with media urls
                if (json['mediaid']) {
                    const mediaUrl = await getMediaUrl(json['mediaid'], datasource.sdk, authorization, tenantName, sitecoreUrlPostfix);
                    if (mediaUrl) {
                        json.src = mediaUrl;
                    }
                }
                // map fields to JSS equivalent
                const mappedFieldsJson = mapToJSSFields(json);
                // generate sample values for empty fields
                const generatedSampleValue = populateField(field);
                // add a mix of sample values and retrieved ones
                const fieldValue = 
                // add an exception for rich text fields. We don't want rich text fields to be parsed, we want the raw xml value
                ((_a = field.type) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'rich text' && item[fieldName].value
                    ? item[fieldName].value
                    : // on the rest of the types, we use the parsed values
                        Object.keys(mappedFieldsJson).length > 0
                            ? {
                                ...(typeof generatedSampleValue === 'object' ? generatedSampleValue : {}),
                                ...mappedFieldsJson
                            }
                            : // on no value, we use generated values
                                item[fieldName].value || generatedSampleValue;
                result[fieldName] = fieldValue;
            }));
            return result;
        }
        else {
            return null;
        }
    }
}
const generateGraphQLFields = (definition) => {
    return definition.reduce((prev, curr) => prev + `${curr.name}: field(name: "${curr.name}"){ value }\n`, '');
};
const pickMostContentfulItem = (results) => {
    let maxContentfulness = 0;
    let mostContentful = {};
    results.forEach((result) => {
        const item = result.innerItem;
        let contentfulness = Object.values(item).filter((field) => field.value !== undefined && field.value !== null && field.value !== '').length;
        if (contentfulness > maxContentfulness) {
            maxContentfulness = contentfulness;
            mostContentful = item;
        }
    });
    return mostContentful;
};
const xmlToJSON = (xml) => {
    const regex = /(\w+)="([^"]*)"/g;
    const attributes = {};
    let match;
    while ((match = regex.exec(xml)) !== null) {
        const [_, attributeName, attributeValue] = match;
        attributes[attributeName] = attributeValue;
    }
    return attributes;
};
const getMediaUrl = async (mediaId, sdk, authorization, tenantName, sitecoreUrlPostfix) => {
    var _a, _b;
    const media = await sdk.fetchJSON(`https://xmc-${tenantName}.${sitecoreUrlPostfix}/sitecore/api/authoring/graphql/v1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authorization },
        body: JSON.stringify({
            query: `query {
        mediaItem(where: {
          mediaItemId : "${mediaId}"
        }) {
          url
        }
      }
    `
        })
    });
    const mediaUrl = (_b = (_a = media === null || media === void 0 ? void 0 : media.data) === null || _a === void 0 ? void 0 : _a.mediaItem) === null || _b === void 0 ? void 0 : _b.url;
    if (!mediaUrl) {
        return null;
    }
    return `https://xmc-${tenantName}.${sitecoreUrlPostfix}` + mediaUrl;
};
/**
 * Maps autoring api fields to JSS euqivalent ones
 */
const mapToJSSFields = (json) => {
    const result = {};
    const mappings = {
        url: 'href'
    };
    for (const key in json) {
        if (json.hasOwnProperty(key) && mappings.hasOwnProperty(key)) {
            const newKey = mappings[key];
            result[newKey] = json[key];
        }
        else {
            result[key] = json[key];
        }
    }
    return result;
};
const populateField = (field) => {
    if (!(field === null || field === void 0 ? void 0 : field.type)) {
        return 'value';
    }
    const type = field.type.toLowerCase();
    switch (type) {
        case 'checkbox':
            return true;
        case 'date':
            return '10-10-2011';
        case 'datetime':
            return '13:12:11 10-10-2010';
        case 'file':
            return {
                src: `https://feaasstatic.blob.core.windows.net/assets/sample/image.svg`,
                title: 'Sample Image',
                displayName: 'Sample Image'
            };
        case 'image':
            return {
                src: `https://feaasstatic.blob.core.windows.net/assets/sample/image.svg`,
                class: `scEmptyImage`,
                alt: ``,
                width: `580`,
                height: `386`
            };
        case 'integer':
            return 10;
        case 'multi-line text':
            return "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letraset sheets containing Lorem Ipsum passages, and more recently with desktop publishing software like Aldus PageMaker including versions of Lorem Ipsum.";
        case 'number':
            return 123;
        case 'password':
            return '***password***';
        case 'rich text':
            return ('<p><b>Example of <i>rich text</i> from <a href="#">XMC data items</a>.</b></p>' +
                '<p>This placeholder will be substituted in Pages</p>');
        case 'single-line text':
            return 'Single line text';
        case 'list':
            return [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }];
        case 'droplist':
            return [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }];
        case 'grouped Droplink':
            return [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }];
        case 'grouped Droplist':
            return [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }];
        case 'lookup Name Lookup Value List':
            return [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }];
        case 'multilist':
            return [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }];
        case 'multilist with Search':
            return [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }];
        case 'droplink':
            return { href: 'https://www.sitecore.com' };
        case 'droptree':
            return { href: 'https://www.sitecore.com' };
        case 'general link':
            return {
                className: 'link-class',
                class: 'link-class',
                title: 'Link Title',
                target: '_blank',
                anchor: '#',
                querystring: '?key=value',
                linktype: 'external',
                href: 'https://www.sitecore.com',
                text: 'description'
            };
        case 'general link with search':
            return {
                className: 'link-class',
                class: 'link-class',
                title: 'Link Title',
                target: '_blank',
                anchor: '#',
                querystring: '?key=value',
                linktype: 'internal',
                href: 'https://www.sitecore.com',
                text: 'description',
                search: 'Search somehting'
            };
        case 'version Identity':
            return { href: 'https://www.sitecore.com', text: 'description' };
        case 'version link':
            return { href: 'https://www.sitecore.com' };
        case 'internal link':
            return {
                className: 'link-class',
                class: 'link-class',
                title: 'Link Title',
                target: '_blank',
                anchor: '#',
                querystring: '?key=value',
                linktype: 'external',
                href: 'https://www.sitecore.com',
                text: 'description'
            };
        case 'icon':
            return {
                src: `https://feaasstatic.blob.core.windows.net/assets/sample/icon.svg`,
                class: `scEmptyImage`,
                alt: ``,
                width: `300`,
                height: `150`
            };
        case 'attachment':
            return { url: `https://feaasstatic.blob.core.windows.net/assets/sample/placeholder.svg` };
        case 'custom':
            return 'custom field';
        case 'datasource':
            return { fieldA: 'fieldA', fieldB: 'fieldB' };
        case 'thumbnail':
            return { src: `https://feaasstatic.blob.core.windows.net/assets/sample/icon.svg` };
        default:
            'value';
    }
};
//# sourceMappingURL=xm-templates.js.map